'use client';

import { useFormContext } from 'react-hook-form';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { SparepartsFormValues } from './spareparts-wrapper';
import type { Model, ComponentGroupItem } from '../../api/spareparts-api';
import { RadioGroup, RadioGroupItem } from '@eliadmin/core/src/components/ui/radio-group';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { getModelAftername } from './model-search-box';
import {
  getComponentGroupTemplates,
  getEngineComponentGroupTemplates,
} from '../../api/spareparts-api';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useState, useEffect, useRef } from 'react';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { Button } from '@eliadmin/core/src/components/ui/button';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { Popover, PopoverContent, PopoverTrigger } from '@eliadmin/core/src/components/ui/popover';
import { FiChevronRight } from 'react-icons/fi';
import FallbackImage from './fallback-image';
import { ModelComponentGroupSection } from './model-component-group-section';

interface SparepartsModelProps {
  className?: string;
  selectedModel?: Model | null;
  selectedComponent?: ComponentGroupItem | null;
  onComponentSelect?: (component: ComponentGroupItem | null) => void;
}

export function SparepartsModel({
  className,
  selectedModel,
  selectedComponent,
  onComponentSelect,
}: SparepartsModelProps) {
  const { t } = useTranslations();
  const form = useFormContext<SparepartsFormValues>();
  const [componentGroups, setComponentGroups] = useState<ComponentGroupItem[]>([]);
  const [isLoadingComponentGroups, setIsLoadingComponentGroups] = useState(false);
  const [componentGroupsError, setComponentGroupsError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);
  const [selectedComponentGroup, setSelectedComponentGroup] = useState<ComponentGroupItem | null>(
    selectedComponent || null,
  );
  const [hasUserGoneBack, setHasUserGoneBack] = useState(false);
  const apiKeys = useGlobalStore(state => state.apiKeys);
  const TEST_API_KEY = (apiKeys as any)?.spareparts || '';
  const category = form.watch('category') || 'frame';
  const previousScope = useRef(`${selectedModel?.articleNumber || ''}:${category}`);

  // Group component groups by first 2 chars of articleNumber
  const groupedComponentGroups = componentGroups.reduce(
    (groups, item) => {
      const groupKey = item.articleNumber.substring(0, 2);
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(item);
      return groups;
    },
    {} as Record<string, ComponentGroupItem[]>,
  );

  // Sort groups numerically
  const sortedGroupEntries = Object.entries(groupedComponentGroups).sort(([a], [b]) => {
    const aNum = parseInt(a, 10);
    const bNum = parseInt(b, 10);
    return aNum - bNum;
  });

  const handleComponentGroupClick = (componentGroup: ComponentGroupItem) => {
    // Reset the hasUserGoneBack flag when user manually clicks on a component group
    setHasUserGoneBack(false);

    // Images come from the documented frame/engine template lists, not a single-template route.
    const currentGroup =
      componentGroups.find(
        item => item.componentGroupTemplateId === componentGroup.componentGroupTemplateId,
      ) || componentGroup;
    setSelectedComponentGroup(currentGroup);
    onComponentSelect?.(currentGroup);
  };

  // Update selected component group when prop changes
  useEffect(() => {
    if (selectedComponent) {
      setSelectedComponentGroup(selectedComponent);
      setHasUserGoneBack(false); // Reset the flag when a new component is selected
    } else {
      // Clear selectedComponentGroup when selectedComponent is null
      setSelectedComponentGroup(null);
    }
  }, [selectedComponent]);

  useEffect(() => {
    let cancelled = false;
    const scope = `${selectedModel?.articleNumber || ''}:${category}`;
    const scopeChanged = previousScope.current !== scope;
    previousScope.current = scope;
    if (scopeChanged) {
      setSelectedComponentGroup(null);
      onComponentSelect?.(null);
    }
    const fetchComponentGroups = async () => {
      setComponentGroups([]);
      setComponentGroupsError(null);
      if (!selectedModel?.articleNumber) {
        setIsLoadingComponentGroups(false);
        return;
      }
      setIsLoadingComponentGroups(true);
      try {
        const result =
          category === 'engine'
            ? await getEngineComponentGroupTemplates(selectedModel.articleNumber, {
                body: { cultureCode: 'en' },
                apiKey: TEST_API_KEY,
              })
            : await getComponentGroupTemplates(selectedModel.articleNumber, {
                body: { cultureCode: 'en' },
                apiKey: TEST_API_KEY,
              });

        if (cancelled) return;
        if (!result.success || !result.data) {
          throw new Error(
            result.message || 'Skupiny dielov sa nepodarilo načítať. Skúste to znova.',
          );
        }

        if (result.success && result.data) {
          // Transform component group templates to a format similar to usage list
          const templates = result.data.componentGroupTemplates || [];
          const transformedItems = templates.map(template => ({
            assetPathHighResolution: template.assetPathHighResolution,
            assetPathMediumResolution: template.assetPathMediumResolution,
            assetPathLowResolution: template.assetPathLowResolution,
            componentGroupId: template.componentGroupId,
            componentGroup: template.componentGroup,
            componentGroupTemplateId: template.id,
            componentGroupDescription:
              template.componentGroup.description ||
              template.componentGroup.code ||
              'Skupina dielov',
            componentGroupType: template.componentGroup.componentType,
            articleName: template.componentGroup.description || '',
            articleNumber: template.componentGroup.code || '',
            modelArticleDescription: selectedModel.description,
            modelArticleNumber: selectedModel.articleNumber,
            modelYear: selectedModel.year || undefined,
            modelVariant: undefined,
          }));

          // Sort by article number (convert string to number for proper sorting)
          const sortedItems = transformedItems.sort((a, b) => {
            const aNum = parseInt(a.articleNumber, 10) || 0;
            const bNum = parseInt(b.articleNumber, 10) || 0;
            return aNum - bNum;
          });

          setComponentGroups(sortedItems as unknown as ComponentGroupItem[]);

          // If we have a selectedComponent from usage list, find and set the matching component group
          // Only do this if we don't already have a selectedComponentGroup and user hasn't gone back
          if (selectedComponent && !hasUserGoneBack && !scopeChanged) {
            // A similarly named group is not necessarily the same drawing.
            const matchingComponentGroup = sortedItems.find(
              item => item.componentGroupTemplateId === selectedComponent.componentGroupTemplateId,
            );

            if (matchingComponentGroup) {
              // If shouldReset is true (from usage list), always update with the fetched component group
              // This ensures we get the component with images from the API
              if ((selectedModel as any)?.shouldReset) {
                // Always update with the fetched component group (it has more complete data than usage list)
                setSelectedComponentGroup(matchingComponentGroup as unknown as ComponentGroupItem);
                // Update the parent component with the fetched version
                onComponentSelect?.(matchingComponentGroup as unknown as ComponentGroupItem);
                // Clear the shouldReset flag after using it
                if (selectedModel) {
                  (selectedModel as any).shouldReset = false;
                }
              } else if (!selectedComponentGroup) {
                // Only set if we don't already have a selectedComponentGroup
                setSelectedComponentGroup(matchingComponentGroup as unknown as ComponentGroupItem);
              }
            }
          }
        } else {
          setComponentGroups([]);
        }
      } catch (error) {
        if (cancelled) return;
        setComponentGroups([]);
        setComponentGroupsError(
          error instanceof Error ? error.message : 'Skupiny dielov sa nepodarilo načítať.',
        );
      } finally {
        if (!cancelled) setIsLoadingComponentGroups(false);
      }
    };

    // Only clear selectedComponentGroup if there's no selectedComponent prop
    // This prevents clearing when a component is selected from usage list
    if (!selectedComponent) {
      setSelectedComponentGroup(null);
    }

    // Reset the hasUserGoneBack flag when model changes
    setHasUserGoneBack(false);

    fetchComponentGroups();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModel?.articleNumber, category, TEST_API_KEY, retryCount]);

  // If no model is selected, dont render anything
  if (!selectedModel) {
    return null;
  }

  // Safety check - ensure selectedModel is a valid object
  if (!selectedModel || typeof selectedModel !== 'object') {
    return null;
  }

  // Get display name safely - ensure we only get string values
  const modelDisplayName = String(
    selectedModel.description || selectedModel.articleNumber || selectedModel.id || 'Unnamed Model',
  );

  // Safety check for getModelAftername - ensure it returns a string
  let modelAftername = '';
  try {
    const afternameResult = getModelAftername(selectedModel as any);
    modelAftername = String(afternameResult || '');
  } catch {
    modelAftername = '';
  }

  return (
    <div
      className={cn(
        'mt-6 min-h-[550px] rounded-lg border border-gray-200 bg-gray-50 p-3 sm:p-4',
        className,
      )}
    >
      <h3 className="mb-2 text-sm font-medium text-gray-700">{t('Selected Model')}</h3>
      <div className="mb-4 flex flex-col text-sm text-primary sm:flex-row">
        <div
          className={cn(selectedComponentGroup && 'cursor-pointer')}
          onClick={() => {
            if (selectedComponentGroup) {
              setSelectedComponentGroup(null);
              setHasUserGoneBack(true);
              // Clear the selectedComponent prop when user clicks on model name
              onComponentSelect?.(null);
            }
          }}
        >
          {modelDisplayName} {modelAftername}
        </div>
        {selectedComponentGroup && (
          <span className="mt-1 font-semibold text-gray-600 sm:ml-2 sm:mt-0">
            {' '}
            &gt; {selectedComponentGroup.componentGroupDescription}
          </span>
        )}
      </div>

      {/* Category Field */}
      <FormField
        control={form.control}
        name="category"
        render={({ field }) => (
          <FormItem className="space-y-2">
            <FormLabel className="text-sm font-medium">{t('Category')}</FormLabel>
            <FormControl>
              <RadioGroup
                value={field.value}
                onValueChange={field.onChange}
                className="flex flex-col space-y-3 sm:flex-row sm:space-x-6 sm:space-y-0"
              >
                <div className="flex items-center space-x-3">
                  <RadioGroupItem
                    value="engine"
                    id="engine-model"
                    className={cn(
                      'h-[21px] w-[21px] rounded-full border-2 border-gray-300 text-primary',
                      'data-[state=checked]:border-primary data-[state=checked]:bg-primary',
                      'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                    )}
                  />
                  <label
                    htmlFor="engine-model"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {t('Engine')}
                  </label>
                </div>
                <div className="flex items-center space-x-3">
                  <RadioGroupItem
                    value="frame"
                    id="frame-model"
                    className={cn(
                      'h-[21px] w-[21px] rounded-full border-2 border-gray-300 text-primary',
                      'data-[state=checked]:border-primary data-[state=checked]:bg-primary',
                      'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                    )}
                  />
                  <label
                    htmlFor="frame-model"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {t('Frame')}
                  </label>
                </div>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Component Group Templates or Selected Component Group */}
      {componentGroupsError ? (
        <div
          role="alert"
          className="space-y-3 rounded border border-red-200 bg-white p-4 text-sm text-red-700"
        >
          <p>{componentGroupsError}</p>
          <Button type="button" variant="outline" onClick={() => setRetryCount(value => value + 1)}>
            Skúsiť znova
          </Button>
        </div>
      ) : selectedComponentGroup ? (
        <ModelComponentGroupSection
          selectedComponentGroup={selectedComponentGroup}
          setSelectedComponentGroup={setSelectedComponentGroup}
          componentGroups={componentGroups}
          onBack={() => {
            setSelectedComponentGroup(null);
            setHasUserGoneBack(true);
            // Clear the selectedComponent prop when user goes back
            onComponentSelect?.(null);
          }}
        />
      ) : (
        <div className="flex flex-col gap-6 lg:flex-row">
          <div className="mt-6 w-full lg:w-[250px]">
            {isLoadingComponentGroups ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Spinner className="h-4 w-4" /> Loading...
              </div>
            ) : componentGroups.length > 0 ? (
              <ul className="space-y-1">
                {componentGroups.map((item: ComponentGroupItem) => (
                  <li
                    key={item.componentGroupTemplateId}
                    className="group relative flex cursor-pointer items-center justify-between rounded px-2 py-1 text-sm text-gray-800 hover:bg-gray-100"
                    onClick={() => handleComponentGroupClick(item)}
                  >
                    <div className="flex flex-1 gap-1">
                      <div className="mt-0.5 w-5 min-w-5 text-center text-gray-500">
                        {item.articleNumber.substring(0, 2)}
                      </div>
                      <div className="flex-1 text-start leading-relaxed">
                        {item.componentGroupDescription}
                      </div>
                    </div>
                    <div className="ml-2 mt-0.5 flex h-full flex-shrink-0 items-center">
                      <FiChevronRight className="text-primary" />
                    </div>

                    {/* High-resolution image on hover - hidden on mobile */}
                    <div className="absolute bottom-0 left-full z-50 ml-2 hidden w-96 rounded-lg border border-gray-200 bg-white p-2 shadow-lg group-hover:block">
                      <FallbackImage
                        src={item.assetPathHighResolution}
                        fallbackSrc={[
                          item.assetPathMediumResolution || '',
                          item.assetPathLowResolution || '',
                          '/images/spareparts/placeholder_ktm.png',
                        ].filter(Boolean)}
                        alt={item.componentGroupDescription}
                        className="h-96 w-96 rounded object-contain"
                        fallbackClassName="h-64 w-64 rounded object-contain"
                        width={384}
                        height={384}
                        fallbackWidth={256}
                        fallbackHeight={256}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-gray-500">No component groups found.</div>
            )}
          </div>
          <div className="mt-6 flex-grow">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3">
              {sortedGroupEntries.map(([groupKey, items]) => (
                <div
                  key={groupKey}
                  className="flex w-full flex-col items-center border border-primary"
                >
                  <Popover
                    open={openPopoverId === groupKey}
                    onOpenChange={open => setOpenPopoverId(open ? groupKey : null)}
                  >
                    <PopoverTrigger asChild>
                      <div
                        className="relative h-24 w-full cursor-pointer sm:h-32"
                        onClick={() => {
                          // This function is no longer needed as Popover handles interaction
                        }}
                      >
                        {items[0]?.componentGroup?.assetPath ? (
                          <Image
                            src={items[0].componentGroup.assetPath}
                            alt={items[0].componentGroupDescription}
                            fill
                            className="rounded-lg border border-gray-200 object-contain"
                            onError={() => {
                              // Fallback to fallback asset path if main asset fails
                              if (items[0].componentGroup?.fallbackAssetPath) {
                                // Note: Next.js Image doesn't support onError for fallback
                                // We'll need to handle this differently if needed
                              }
                            }}
                          />
                        ) : (
                          <div className="flex h-24 w-full items-center justify-center rounded-lg border border-gray-200 bg-gray-100 sm:h-32">
                            <span className="text-xs text-gray-400">No Image</span>
                          </div>
                        )}
                        <div className="absolute right-0 top-0 flex h-6 w-6 items-center justify-center rounded-full bg-orange-500 text-xs text-white">
                          {groupKey}
                        </div>
                      </div>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-4 sm:w-80">
                      <div className="space-y-2">
                        {items.map(item => (
                          <div
                            key={item.componentGroupTemplateId}
                            className="cursor-pointer rounded px-2 py-1 text-sm font-semibold text-primary hover:bg-gray-100"
                            onClick={() => {
                              handleComponentGroupClick(item);
                              setOpenPopoverId(null);
                            }}
                          >
                            {item.componentGroupDescription}
                          </div>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
